import React, { useEffect } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import _isEmpty from 'lodash/isEmpty';

import {
  Button,
  Select,
  TextField,
  Typography,
} from '../../../components/atoms';
import { Dialog } from '../../../components/layouts';
import {
  CollectionLocationArea,
  PickupLocationOption,
  TableServiceCategory,
} from '../../../core/api/restaurant/restaurant.types';
import { checkoutUtils } from '../utils';

interface Props {
  handleClose: () => any;
  handleNumber: (e: any) => any;
  handleSubmit: () => any;
  pickupLocationOptions: PickupLocationOption[];
  locationAreas: CollectionLocationArea[];
  placeOrderAfterTableSelect: boolean;
  showTableServiceDialog: boolean;
  tableNumber: string;
  tableServiceCategory: TableServiceCategory;
  translate: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiSelect-root': {
      padding: theme.spacing(1.75, 1.5),
      width: '100%',
    },
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(2, 2.5, 7),
    },
    '& .fd-text__h1': {
      marginBottom: theme.spacing(1.75),

      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(2.5),
      },
    },
  },

  selector: {
    marginBottom: theme.spacing(2),

    '& .MuiSelect-selectMenu, .MuiSelect-select': {
      padding: theme.spacing(1.625),
    },
    '&.MuiInputBase-root': {
      border: `1px solid ${theme.palette.grey[400]}`,
      position: 'relative',

      '&.Mui-focused': {
        border: `1px solid ${theme.palette.common.black}`,
      },
      '& .MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd': {
        position: 'absolute',
      },
      '& .MuiInputAdornment-positionStart': {
        left: theme.spacing(1.5),
      },
      '& .MuiSelect-nativeInput': {
        marginLeft: theme.spacing(1.5),
      },
      '& .MuiSvgIcon-root': {
        fill: theme.palette.common.black,
        right: theme.spacing(1),
      },
    },
  },
}));

export function TableSelectDialog(props: Props): JSX.Element {
  const {
    handleClose,
    handleNumber,
    handleSubmit,
    tableNumber,
    pickupLocationOptions,
    locationAreas,
    placeOrderAfterTableSelect,
    showTableServiceDialog,
    tableServiceCategory,
    translate,
  } = props;
  const classes = useStyles();

  useEffect(() => {
    const handleLocationAreaSelection = () => {
      const locationAreaWithLocations = locationAreas.findIndex(
        (area) => area.Locations.length > 0,
      );

      if (locationAreaWithLocations !== -1 || undefined) {
        handleNumber({
          target: {
            value:
              locationAreas[
                locationAreaWithLocations
              ].Locations[0].LocationId.toString(),
          },
        });
      }
    };

    const handlePickupOptionSelection = () => {
      const firstRealOptionIndex = pickupLocationOptions.findIndex(
        (pickupLocationOption) => !pickupLocationOption.Value.startsWith('---'),
      );

      if (firstRealOptionIndex !== -1 || undefined) {
        handleNumber({
          target: {
            value:
              pickupLocationOptions[
                firstRealOptionIndex
              ].PickupLocationOptionId.toString(),
          },
        });
      }
    };

    if (showTableServiceDialog && !tableNumber) {
      if (!_isEmpty(locationAreas)) {
        handleLocationAreaSelection();
      } else if (!_isEmpty(pickupLocationOptions)) {
        handlePickupOptionSelection();
      }
    }
  }, [
    tableNumber,
    pickupLocationOptions,
    locationAreas,
    showTableServiceDialog,
  ]);

  const dialogActions = (
    <Button
      disabled={!tableNumber}
      fullWidth
      key="table-select-button"
      onClick={placeOrderAfterTableSelect ? handleSubmit : handleClose}
    >
      {placeOrderAfterTableSelect
        ? translate('Place_order')
        : translate(`Select_table_${tableServiceCategory}`)}
    </Button>
  );

  const tableListOptions = checkoutUtils.getTableListOptions(
    locationAreas,
    pickupLocationOptions,
  );

  let selectedOrDefaultOption;
  if (tableListOptions) {
    const tableNumberOrDefault = tableNumber || '';
    const selectedOption = tableListOptions.find(
      (option) => option.key === tableNumberOrDefault.toString(),
    );
    selectedOrDefaultOption = selectedOption || null;
  }

  const hasTableListOptions = !_isEmpty(tableListOptions);

  return (
    <Dialog
      actions={dialogActions}
      className={classes.dialog}
      dialogName="table-select"
      onTitleClick={handleClose}
      open={showTableServiceDialog}
    >
      <Typography variant="h1">
        {translate(`Enter_table_number_${tableServiceCategory}`)}
      </Typography>

      {hasTableListOptions ? (
        <Select
          autoFocus
          className={classes.selector}
          id={tableNumber}
          key="table-select-dropdown"
          menuItems={tableListOptions}
          onChange={handleNumber}
          placeholder={translate(`Select_table_number_${tableServiceCategory}`)}
          selected={selectedOrDefaultOption}
        />
      ) : (
        <TextField
          autoFocus
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          onChange={handleNumber}
          placeholder={translate(`Table_number_${tableServiceCategory}`)}
          value={tableNumber || ''}
        />
      )}
    </Dialog>
  );
}
